/**
 * Text search across cloud entities.
 *
 * Three modes:
 *   - title    — keyword match against the title-like fields of all entity types
 *   - content  — full text scan (title + body + attachment descriptions)
 *   - memory   — delegates to the backend `search_memory` tool via a one-off chat session
 *
 * Each runner pushes progress through `onProgress` so the UI can show live updates,
 * and respects a mutable `{ cancelled: boolean }` flag for cooperative cancellation.
 */

import type { CloudNote, CloudTask, ChatSession } from '../types'
import {
  listCloudNotes,
  listCloudTasks,
  listCloudSessions,
  searchMemory,
} from './api'

export type SearchType = 'title' | 'content' | 'memory'

export type SearchResultType =
  | 'chat'
  | 'task'
  | 'note'
  | 'voice'
  | 'image'
  | 'document'
  | 'followup'
  | 'postpone'
  | 'proactive'
  | 'briefing'
  | 'bulletin'
  | 'alert'
  | 'box'
  | 'memory'

export interface SearchResult {
  id: string
  type: SearchResultType
  title: string
  /** Excerpt from the matched field, with a tiny window around the keyword. */
  snippet?: string
  /** AppView name to navigate to when the result is clicked. */
  view: string
}

export interface SearchProgress {
  /** Human-readable category currently being scanned (or status string). */
  scanning: string
  scanned: number
  total: number
  /** Results found so far — pass a fresh array each tick so React notices the change. */
  results: SearchResult[]
  done: boolean
  error?: string
}

export interface CancelToken {
  cancelled: boolean
}

export interface SearchOpts {
  query: string
  type: SearchType
  /** Persona used to seed the chat session for memory search. Defaults to 'pipar'. */
  personaId?: string
  signal: CancelToken
  onProgress: (p: SearchProgress) => void
}

const SNIPPET_RADIUS = 60

function makeSnippet(text: string, query: string): string {
  if (!text) return ''
  const lower = text.toLowerCase()
  const q = query.toLowerCase()
  const idx = lower.indexOf(q)
  if (idx < 0) return text.slice(0, 140)
  const start = Math.max(0, idx - SNIPPET_RADIUS)
  const end = Math.min(text.length, idx + q.length + SNIPPET_RADIUS)
  const prefix = start > 0 ? '…' : ''
  const suffix = end < text.length ? '…' : ''
  return `${prefix}${text.slice(start, end)}${suffix}`
}

function hasMatch(text: string | undefined | null, q: string): boolean {
  if (!text) return false
  return text.toLowerCase().includes(q)
}

/** Best matching field for snippet purposes — prefers the longer / more specific text. */
function pickFirstMatch(fields: Array<string | undefined | null>, q: string): string | null {
  for (const f of fields) {
    if (f && f.toLowerCase().includes(q)) return f
  }
  return null
}

interface ScanCategory {
  name: string
  items: unknown[]
  scan: (item: unknown) => SearchResult | null
}

async function runEntitySearch(opts: SearchOpts): Promise<SearchResult[]> {
  const { query, type, signal, onProgress } = opts
  const q = query.toLowerCase().trim()
  const results: SearchResult[] = []

  onProgress({ scanning: 'Loading entities…', scanned: 0, total: 0, results: [], done: false })

  const [sessionsResp, tasksResp, notesResp] = await Promise.allSettled([
    listCloudSessions(),
    listCloudTasks(),
    listCloudNotes(),
  ])

  if (signal.cancelled) {
    onProgress({ scanning: 'Cancelled', scanned: 0, total: 0, results: [], done: true })
    return results
  }

  const sessions: ChatSession[] = sessionsResp.status === 'fulfilled' ? sessionsResp.value.sessions : []
  const tasks: CloudTask[] = tasksResp.status === 'fulfilled' ? tasksResp.value : []
  const notes: CloudNote[] = notesResp.status === 'fulfilled' ? notesResp.value : []

  // Partition notes by NoteType so each result is labelled correctly and the
  // click handler routes to a view that actually renders it.
  // NoteType: 0=Text, 1=Voice, 2=Image, 3=Mixed, 4=Follow-up, 5=Alert,
  //           6=Postpone, 7=Bulletin, 8=Box, 9=Proactive, 10=Briefing
  const isAlertNote = (n: CloudNote) => {
    if (n.type !== 5) return false
    const owner = (n.metadata as Record<string, unknown> | undefined)?.owner as string | undefined
    return !(typeof owner === 'string' && owner.startsWith('task_'))
  }
  const followups = notes.filter((n) => n.type === 4)
  const postpones = notes.filter((n) => n.type === 6)
  const proactives = notes.filter((n) => n.type === 9)
  const briefings = notes.filter((n) => n.type === 10)
  const bulletins = notes.filter((n) => n.type === 7)
  const boxes = notes.filter((n) => n.type === 8)
  const alerts = notes.filter(isAlertNote)
  const images = notes.filter((n) => n.is_ai_generated_image === true)
  const documents = notes.filter((n) => n.is_ai_generated_document === true || n.is_generated_pdf === true)
  // Voice notes get their own bucket (FilesView shows them under the "Voice" tab).
  const voiceNotes = notes.filter(
    (n) => n.type === 1 && !n.is_ai_generated_image && !n.is_ai_generated_document && !n.is_generated_pdf,
  )
  // "Plain" notes are types 0/2/3 that aren't AI images / AI documents / PDFs.
  const generalNotes = notes.filter(
    (n) =>
      (n.type === 0 || n.type === 2 || n.type === 3) &&
      !n.is_ai_generated_image &&
      !n.is_ai_generated_document &&
      !n.is_generated_pdf,
  )

  const isContent = type === 'content'

  // Helper for follow-up-family notes (4/6/9/10) — they all share a common
  // JSON content shape with `title`, `description`, `note`, `persona_name`.
  const scanJsonFollowupNote = (
    n: CloudNote,
    resultType: SearchResultType,
    view: string,
    fallbackTitle: string,
  ): SearchResult | null => {
    const parsed = (() => {
      try { return JSON.parse(n.content || '{}') as Record<string, unknown> }
      catch { return {} }
    })()
    const titleFields = [n.title, parsed.title as string | undefined]
    const contentFields = [
      ...titleFields,
      parsed.description as string | undefined,
      parsed.note as string | undefined,
      parsed.persona_name as string | undefined,
      n.content,
    ]
    const found = pickFirstMatch(isContent ? contentFields : titleFields, q)
    if (!found) return null
    return {
      id: n.id,
      type: resultType,
      title: n.title || (parsed.title as string | undefined) || fallbackTitle,
      snippet: makeSnippet(found, query),
      view,
    }
  }

  const categories: ScanCategory[] = [
    {
      name: 'Chats',
      items: sessions,
      scan: (raw) => {
        const s = raw as ChatSession
        // Sessions don't expose body text in the list endpoint; both modes scan the summary.
        const found = pickFirstMatch([s.summary], q)
        if (!found) return null
        return {
          id: s.id,
          type: 'chat',
          title: s.summary || '(untitled chat)',
          snippet: makeSnippet(found, query),
          view: 'chat',
        }
      },
    },
    {
      name: 'Tasks',
      items: tasks,
      scan: (raw) => {
        const t = raw as CloudTask
        const fields = isContent
          ? [t.title, t.description as string | undefined, (t.location as string | undefined)]
          : [t.title]
        const found = pickFirstMatch(fields, q)
        if (!found) return null
        return {
          id: t.id,
          type: 'task',
          title: t.title || '(untitled task)',
          snippet: makeSnippet(found, query),
          view: 'tasks',
        }
      },
    },
    {
      name: 'Notes',
      items: generalNotes,
      scan: (raw) => {
        const n = raw as CloudNote
        const fields = isContent ? [n.title, n.content, n.transcription] : [n.title]
        const found = pickFirstMatch(fields, q)
        if (!found) return null
        return {
          id: n.id,
          type: 'note',
          title: n.title || '(untitled note)',
          snippet: makeSnippet(found, query),
          view: 'notes',
        }
      },
    },
    {
      name: 'Images',
      items: images,
      scan: (raw) => {
        const n = raw as CloudNote
        const attachmentText = (n.attachments || [])
          .map((a) => (a.description as string | undefined) || '')
          .filter(Boolean)
          .join(' ')
        const fields = isContent ? [n.title, attachmentText] : [n.title]
        const found = pickFirstMatch(fields, q)
        if (!found) return null
        return {
          id: n.id,
          type: 'image',
          title: n.title || '(untitled image)',
          snippet: makeSnippet(found, query),
          view: 'files',
        }
      },
    },
    {
      name: 'Documents',
      items: documents,
      scan: (raw) => {
        const n = raw as CloudNote
        const fields = isContent ? [n.title, n.content] : [n.title]
        const found = pickFirstMatch(fields, q)
        if (!found) return null
        return {
          id: n.id,
          type: 'document',
          title: n.title || '(untitled document)',
          snippet: makeSnippet(found, query),
          view: 'files',
        }
      },
    },
    {
      name: 'Voice notes',
      items: voiceNotes,
      scan: (raw) => {
        const n = raw as CloudNote
        const fields = isContent ? [n.title, n.content, n.transcription] : [n.title]
        const found = pickFirstMatch(fields, q)
        if (!found) return null
        return {
          id: n.id,
          type: 'voice',
          title: n.title || '(untitled voice note)',
          snippet: makeSnippet(found, query),
          view: 'files',
        }
      },
    },
    {
      name: 'Follow-ups',
      items: followups,
      scan: (raw) => scanJsonFollowupNote(raw as CloudNote, 'followup', 'calls', '(untitled follow-up)'),
    },
    {
      name: 'Postpone reminders',
      items: postpones,
      scan: (raw) => scanJsonFollowupNote(raw as CloudNote, 'postpone', 'calls', '(postpone reminder)'),
    },
    {
      name: 'Proactive reminders',
      items: proactives,
      scan: (raw) => scanJsonFollowupNote(raw as CloudNote, 'proactive', 'calls', '(proactive reminder)'),
    },
    {
      name: 'Morning briefings',
      items: briefings,
      scan: (raw) => scanJsonFollowupNote(raw as CloudNote, 'briefing', 'calls', '(morning briefing)'),
    },
    {
      name: 'Alerts',
      items: alerts,
      scan: (raw) => {
        const n = raw as CloudNote
        const parsed = (() => {
          try { return JSON.parse(n.content || '{}') as Record<string, unknown> }
          catch { return {} }
        })()
        const titleFields = [n.title, parsed.title as string | undefined]
        const contentFields = [...titleFields, parsed.note as string | undefined, n.content]
        const found = pickFirstMatch(isContent ? contentFields : titleFields, q)
        if (!found) return null
        return {
          id: n.id,
          type: 'alert',
          title: n.title || (parsed.title as string | undefined) || '(untitled alert)',
          snippet: makeSnippet(found, query),
          view: 'alerts',
        }
      },
    },
    {
      name: 'Bulletins',
      items: bulletins,
      scan: (raw) => {
        const n = raw as CloudNote
        const fields = isContent ? [n.title, n.content] : [n.title]
        const found = pickFirstMatch(fields, q)
        if (!found) return null
        return {
          id: n.id,
          type: 'bulletin',
          title: n.title || '(untitled bulletin)',
          snippet: makeSnippet(found, query),
          // No dedicated bulletin view in the web client — fall back to BoxView,
          // which is the closest neighbouring "ticket-style" container.
          view: 'box',
        }
      },
    },
    {
      name: 'Box items',
      items: boxes,
      scan: (raw) => {
        const n = raw as CloudNote
        const fields = isContent ? [n.title, n.content] : [n.title]
        const found = pickFirstMatch(fields, q)
        if (!found) return null
        return {
          id: n.id,
          type: 'box',
          title: n.title || '(untitled box item)',
          snippet: makeSnippet(found, query),
          view: 'box',
        }
      },
    },
  ]

  const totalItems = categories.reduce((acc, c) => acc + c.items.length, 0)
  let scanned = 0

  for (const category of categories) {
    if (signal.cancelled) break

    // Emit a category-start tick so the UI label updates even for empty categories.
    onProgress({ scanning: category.name, scanned, total: totalItems, results: [...results], done: false })

    for (let i = 0; i < category.items.length; i++) {
      if (signal.cancelled) break
      const res = category.scan(category.items[i])
      if (res) results.push(res)
      scanned++
      // Yield to the event loop every 25 items so the UI stays responsive on large sets.
      if (scanned % 25 === 0 || i === category.items.length - 1) {
        onProgress({
          scanning: category.name,
          scanned,
          total: totalItems,
          results: [...results],
          done: false,
        })
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 0))
      }
    }
  }

  onProgress({
    scanning: signal.cancelled ? 'Cancelled' : 'Complete',
    scanned,
    total: totalItems,
    results: [...results],
    done: true,
  })
  return results
}

async function runMemorySearch(opts: SearchOpts): Promise<SearchResult[]> {
  const { query, signal, onProgress } = opts
  const persona = opts.personaId || 'pipar'

  onProgress({ scanning: 'Searching memory…', scanned: 0, total: 1, results: [], done: false })

  try {
    const { results: matches, summary } = await searchMemory(query, persona, true)
    if (signal.cancelled) {
      onProgress({ scanning: 'Cancelled', scanned: 0, total: 1, results: [], done: true })
      return []
    }

    const results: SearchResult[] = []

    // Show only the AI-composed answer. Raw chunks are intentionally hidden —
    // they were only useful when there was no synthesized answer. Fall back
    // to showing chunks if the summary is missing (e.g. summarization failed).
    if (summary && summary.trim().length > 0) {
      results.push({
        id: 'memory-summary',
        type: 'memory',
        title: 'Answer',
        snippet: summary.trim(),
        view: 'chat',
      })
    } else {
      for (const item of matches) {
        results.push({
          id: crypto.randomUUID(),
          type: 'memory',
          title: item.source || item.persona || 'Memory match',
          snippet: item.text || '',
          view: 'chat',
        })
      }
    }

    onProgress({
      scanning: 'Complete',
      scanned: 1,
      total: 1,
      results,
      done: true,
    })
    return results
  } catch (e) {
    onProgress({
      scanning: 'Error',
      scanned: 0,
      total: 1,
      results: [],
      done: true,
      error: (e as Error).message,
    })
    return []
  }
}

export async function runSearch(opts: SearchOpts): Promise<SearchResult[]> {
  if (opts.type === 'memory') return runMemorySearch(opts)
  return runEntitySearch(opts)
}
